import { buildApiUrl } from '../../config/api';
import authService from '../../modules/auth/services/authService';

/**
 * Servicio centralizado para logging de errores del frontend
 * Captura contexto detallado y envía errores al backend
 */
class ErrorLogger {
    constructor() {
        // Cache de errores recientes para throttling
        this.errorCache = new Map();
        this.CACHE_DURATION = 60000; // 1 minuto
        this.MAX_STACK_TRACE_LINES = 50;
        this.MAX_PAYLOAD_SIZE = 2000; // caracteres

        // Detectar si debe enviar errores al backend
        // Opción 1: Variable de entorno personalizada (prioridad)
        // Opción 2: Detectar por URL (si no es localhost)
        const enableReporting = process.env.REACT_APP_ENABLE_ERROR_REPORTING;
        if (enableReporting !== undefined) {
            this.shouldSendToBackend = enableReporting === 'true' || enableReporting === '1';
        } else {
            // Por defecto, intentar enviar si no es localhost
            const isLocalhost = window.location.hostname === 'localhost' ||
                window.location.hostname === '127.0.0.1' ||
                window.location.hostname === '';
            this.shouldSendToBackend = !isLocalhost;
        }
    }

    /**
     * Obtiene el contexto del error actual
     */
    getErrorContext(error, errorInfo = null) {
        const context = {
            url: window.location.href,
            path: window.location.pathname,
            search: window.location.search,
            hash: window.location.hash,
            user_agent: navigator.userAgent,
            timestamp: new Date().toISOString(),
            language: navigator.language,
            platform: navigator.platform,
            screen_resolution: `${window.screen.width}x${window.screen.height}`,
        };

        // Información del usuario si está autenticado
        try {
            const userDataStr = localStorage.getItem('user_data');
            if (userDataStr) {
                const userData = JSON.parse(userDataStr);
                context.user_email = userData.email || null;
                // No incluir token ni datos sensibles
            }
        } catch (e) {
            // Ignorar errores al leer user_data
        }

        // Información del error
        if (error) {
            context.error_name = error.name || 'Error';
            context.error_message = error.message || 'Unknown error';
            context.error_stack = this.formatStackTrace(error.stack || '');

            // Si es un error de red, capturar información adicional
            if (error instanceof TypeError && error.message.includes('fetch')) {
                context.error_type = 'network_error';
            }
        }

        // Si hay errorInfo de React ErrorBoundary
        if (errorInfo) {
            context.component_stack = this.formatStackTrace(
                errorInfo.componentStack || ''
            );
        }

        // Referrer
        if (document.referrer) {
            context.referrer = document.referrer;
        }

        return context;
    }

    /**
     * Formatea el stack trace limitando su tamaño
     */
    formatStackTrace(stack) {
        if (!stack || typeof stack !== 'string') {
            return '';
        }

        const lines = stack.split('\n');
        if (lines.length > this.MAX_STACK_TRACE_LINES) {
            return lines.slice(0, this.MAX_STACK_TRACE_LINES).join('\n') + '\n... [truncated]';
        }

        return stack;
    }

    /**
     * Filtra información sensible de un objeto
     */
    filterSensitiveData(data, depth = 0) {
        // Limitar profundidad
        if (depth > 5) {
            return '[max_depth_reached]';
        }

        if (data === null || data === undefined) {
            return data;
        }

        if (typeof data !== 'object') {
            return data;
        }

        if (Array.isArray(data)) {
            return data.map(item => this.filterSensitiveData(item, depth + 1));
        }

        const sensitiveKeys = [
            'password',
            'token',
            'secret',
            'key',
            'api_key',
            'auth_token',
            'access_token',
            'refresh_token',
            'authorization',
            'cookie',
        ];

        const filtered = {};

        for (const [key, value] of Object.entries(data)) {
            const lowerKey = key.toLowerCase();
            const isSensitive = sensitiveKeys.some(sensitiveKey =>
                lowerKey.includes(sensitiveKey)
            );

            if (isSensitive) {
                filtered[key] = '[FILTERED]';
            } else if (typeof value === 'object') {
                filtered[key] = this.filterSensitiveData(value, depth + 1);
            } else {
                filtered[key] = value;
            }
        }

        return filtered;
    }

    /**
     * Genera una clave única para un error (para throttling)
     */
    getErrorKey(error, context) {
        const message = error?.message || '';
        const stack = error?.stack || '';
        const path = context.path || '';

        // Usar primeros 100 caracteres del mensaje y path
        return `${message.substring(0, 100)}|${path}|${stack.substring(0, 200)}`;
    }

    /**
     * Verifica si un error debe ser ignorado (throttling)
     */
    shouldSkipError(error, context) {
        const errorKey = this.getErrorKey(error, context);
        const now = Date.now();

        if (this.errorCache.has(errorKey)) {
            const { timestamp } = this.errorCache.get(errorKey);
            if (now - timestamp < this.CACHE_DURATION) {
                return true; // Ignorar error duplicado reciente
            }
        }

        // Guardar en cache
        this.errorCache.set(errorKey, { timestamp: now });

        // Limpiar cache antiguo
        for (const [key, value] of this.errorCache.entries()) {
            if (now - value.timestamp > this.CACHE_DURATION) {
                this.errorCache.delete(key);
            }
        }

        return false;
    }

    /**
     * Prepara el payload del error para enviar
     */
    prepareErrorPayload(error, errorInfo = null) {
        const context = this.getErrorContext(error, errorInfo);

        const payload = {
            error: {
                name: error?.name || 'Error',
                message: error?.message || 'Unknown error',
                stack: context.error_stack,
            },
            context: this.filterSensitiveData(context),
            source: 'frontend',
        };

        if (errorInfo) {
            payload.error.component_stack = context.component_stack;
        }

        // Limitar tamaño total del payload
        const payloadStr = JSON.stringify(payload);
        if (payloadStr.length > this.MAX_PAYLOAD_SIZE) {
            // Truncar stack trace si es necesario
            if (payload.error.stack) {
                const maxStackSize = this.MAX_PAYLOAD_SIZE - payloadStr.length + payload.error.stack.length - 500;
                if (maxStackSize > 0) {
                    payload.error.stack = payload.error.stack.substring(0, maxStackSize) + '... [truncated]';
                }
            }
        }

        return payload;
    }

    /**
     * Envía el error al backend
     */
    async sendErrorToBackend(payload) {
        try {
            const url = buildApiUrl('/api/user/errors');

            const options = {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(payload),
            };

            // Si hay token, incluirlo en headers
            const token = authService.getToken();
            if (token) {
                options.headers['Authorization'] = `Bearer ${token}`;
            }

            const response = await fetch(url, options);

            if (!response.ok) {
                // eslint-disable-next-line no-console
                console.warn('Failed to send error to backend:', response.status);
            }
        } catch (sendError) {
            // No hacer nada si falla el envío, solo loguear en consola
            // eslint-disable-next-line no-console
            console.warn('Could not send error to backend:', sendError);
        }
    }

    /**
     * Loguea un error
     */
    async logError(error, errorInfo = null) {
        try {
            // Preparar contexto
            const context = this.getErrorContext(error, errorInfo);

            // Throttling: evitar errores duplicados
            if (this.shouldSkipError(error, context)) {
                return;
            }

            // Preparar payload
            const payload = this.prepareErrorPayload(error, errorInfo);

            // Siempre mostrar en consola para debugging
            // eslint-disable-next-line no-console
            console.error('Error logged:', payload);

            // Si está configurado para enviar al backend, intentar enviar
            if (this.shouldSendToBackend) {
                await this.sendErrorToBackend(payload);
            }
        } catch (logError) {
            // Si falla el logging, solo loguear en consola
            // eslint-disable-next-line no-console
            console.error('Error logging failed:', logError);
            // eslint-disable-next-line no-console
            console.error('Original error:', error);
        }
    }

    /**
     * Loguea un error de JavaScript no capturado
     */
    logUnhandledError(message, source, lineno, colno, error) {
        const errorObj = error || new Error(message);
        if (error) {
            errorObj.message = `${message} at ${source}:${lineno}:${colno}`;
        }
        this.logError(errorObj);
    }

    /**
     * Loguea una promesa rechazada no manejada
     */
    logUnhandledRejection(event) {
        const error = event.reason instanceof Error
            ? event.reason
            : new Error(String(event.reason));
        this.logError(error);
    }
}

// Crear instancia singleton
const errorLogger = new ErrorLogger();

export default errorLogger;

